
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { updateRewardClaimStatus } from "./referralApi";
import { ReferralReward } from "./types";
import { supabase } from "@/integrations/supabase/client";

export const useClaimReward = (
  rewards: ReferralReward[],
  setRewards: React.Dispatch<React.SetStateAction<ReferralReward[]>>
) => {
  const { toast } = useToast();
  const [claimingRewards, setClaimingRewards] = useState<Record<string, boolean>>({});

  const claimReward = async (rewardId: string): Promise<boolean> => {
    setClaimingRewards(prev => ({ ...prev, [rewardId]: true }));
    
    try {
      // Find the reward to get its type
      const reward = rewards.find(r => r.id === rewardId);
      if (!reward) throw new Error("Reward not found");
      
      // If this is a free_month reward type, call the apply-referral-month function
      if (reward.rewardType === 'free_month') {
        console.log("Processing free month reward");
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("User not authenticated");
        
        // Get the current session to pass the auth token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("No active session");
        
        // Call the edge function to apply the free month with auth token
        const { data, error } = await supabase.functions.invoke('apply-referral-month', {
          body: {
            userId: user.id,
            rewardId: rewardId
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });
        
        if (error) throw error;
        console.log("Free month applied successfully:", data);
        
        // Toast specific success message for free month
        toast({
          title: "Free Month Added!",
          description: "Your free month has been applied to your subscription.",
        });
      } else {
        // For other reward types, just mark as claimed
        await updateRewardClaimStatus(rewardId);
        
        toast({
          title: "Reward Claimed!",
          description: "Your reward has been claimed successfully.",
        });
      }
      
      // Update the local state
      setRewards(prev => prev.map(reward => 
        reward.id === rewardId 
          ? { ...reward, claimed: true, claimedDate: new Date().toISOString() } 
          : reward
      ));
      
      return true;
    } catch (error) {
      console.error("Error claiming reward:", error);
      toast({
        title: "Error",
        description: "Failed to claim reward",
        variant: "destructive"
      });
      return false;
    } finally {
      setClaimingRewards(prev => ({ ...prev, [rewardId]: false }));
    }
  };

  return { claimReward, claimingRewards };
};
